import { useCallback, useRef, useState } from "react";
import {
  uploadOcrDocument,
  type OcrResponse,
} from "../api/client";

interface UploadStatus {
  loading: boolean;
  error: string | null;
  info: string | null;
}

const initialStatus: UploadStatus = {
  loading: false,
  error: null,
  info: null,
};

const InternalToolsPage = () => {
  const [ocrStatus, setOcrStatus] = useState<UploadStatus>(initialStatus);
  const [ocrText, setOcrText] = useState("");
  const ocrInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileSelection = useCallback(
    (files: FileList | null, fallbackRef: React.RefObject<HTMLInputElement>) => {
      if (!files || files.length === 0) {
        fallbackRef.current?.click();
        return null;
      }
      return files[0];
    },
    []
  );

  const handleOcrUpload = useCallback(
    async (file: File) => {
      setOcrStatus({ loading: true, error: null, info: null });

      try {
        const response: OcrResponse = await uploadOcrDocument(file);
        setOcrText(response.text ?? "");

        const message = response.email?.delivered
          ? "OCR-Ergebnis wurde per E-Mail verschickt."
          : response.email?.message ?? null;

        setOcrStatus({ loading: false, error: null, info: message });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "OCR-Verarbeitung fehlgeschlagen.";
        setOcrStatus({ loading: false, error: message, info: null });
      }
    },
    []
  );

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const file = event.dataTransfer.files?.[0];
      if (file) {
        await handleOcrUpload(file);
      }
    },
    [handleOcrUpload]
  );

  const isOcrActionDisabled = ocrStatus.loading;

  return (
    <div className="internal-page">
      <div className="schwyz-cross" aria-hidden="true"><span /></div>

      <header className="internal-page__header">
        <h1 className="internal-page__title internal-page__title--register">
          Handelsregister des Kantons Schwyz
        </h1>
        <h1 className="internal-page__title">OCR-Erkennung</h1>
      </header>

      <p className="internal-page__subtitle">
        Ziehen Sie Fotos oder PDF-Dateien hierher, um den reinen Text über die Mistral-OCR-API zu extrahieren.
      </p>

      <section className="internal-tools-grid">
        <article className="tool-card">
          <header className="tool-card__header">
            <h2>Dokument verarbeiten</h2>
            <p>
              Unterstützt werden PDF-Dateien sowie Bilddateien. Hochgeladene Dateien werden nach der Verarbeitung wieder gelöscht.
            </p>
          </header>

          <div
            className="dropzone"
            onDrop={handleDrop}
            onDragOver={(event) => event.preventDefault()}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                ocrInputRef.current?.click();
              }
            }}
          >
            <p className="dropzone__text">Datei hier ablegen oder auswählen…</p>
            <button
              type="button"
              className="button secondary"
              disabled={isOcrActionDisabled}
              onClick={() => ocrInputRef.current?.click()}
            >
              Datei auswählen
            </button>

            <input
              ref={ocrInputRef}
              type="file"
              accept=".pdf,image/*"
              hidden
              onChange={async (event) => {
                const file = handleFileSelection(event.target.files, ocrInputRef);
                if (file) {
                  await handleOcrUpload(file);
                  event.target.value = "";
                }
              }}
            />
          </div>
{ocrStatus.info && <p className="tool-card__info">{ocrStatus.info}</p>}
          {ocrStatus.error && <p className="tool-card__error">{ocrStatus.error}</p>}

          <textarea
            className="tool-card__result"
            value={ocrText}
            onChange={(event) => setOcrText(event.target.value)}
            placeholder="Der erkannte Text erscheint hier…"
            rows={14}
          />
        </article>
      </section>
    </div>
  );
};

export default InternalToolsPage;
